import React, { useCallback, useEffect, useRef, useState } from "react";
import toastr from "toastr";
import { FaLock, FaTrashAlt, FaUpload } from "react-icons/fa";
import BrokerSkeleton from "../../components/Broker/BrokerSkeleton";
import BrokerEmptyState from "../../components/Broker/BrokerEmptyState";
import { doDELETE, doPOST } from "../../util/HttpUtil";
import { brokerLogoSrc } from "../../util/brokerAuth";
import { BrokerService, getBrokerProfile, syncBrokerProfileLogo } from "./BrokerService";

const BROKER_LOGO_FIELD = "logoAttachment";

const MAX_LOGO_BYTES = 2 * 1024 * 1024; // 2 MB

const normalizeDomain = (value) => String(value || "").trim().toLowerCase().replace(/^@/, "");
const titleCase = (value) => String(value || "active").replace(/[_-]+/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

const formatDate = (value) => {
  if (!value) return "Not available";
  const numeric = Number(value);
  const date = Number.isFinite(numeric) ? new Date(numeric < 1e12 ? numeric * 1000 : numeric) : new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const initials = (value) => {
  const parts = String(value || "Partner").split(/[^A-Za-z0-9]+/).filter(Boolean);
  return (parts.length ? parts.slice(0, 2).map((part) => part[0]).join("") : "B").toUpperCase();
};

// Authenticated logo preview: broker logos served from /api need the bearer token.
const LogoPreview = ({ src, fallback }) => {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let objectUrl = "";
    let cancelled = false;
    const run = async () => {
      if (!src) { setUrl(""); return; }
      if (!String(src).startsWith("/api")) { setUrl(src); return; }
      try {
        const token = localStorage.getItem("BearerToken");
        const response = await fetch(src, { headers: token ? { Authorization: token } : {}, credentials: "include" });
        if (!response.ok) throw new Error("logo");
        const blob = await response.blob();
        objectUrl = window.URL.createObjectURL(blob);
        if (!cancelled) setUrl(objectUrl);
      } catch { if (!cancelled) setUrl(""); }
    };
    run();
    return () => { cancelled = true; if (objectUrl) window.URL.revokeObjectURL(objectUrl); };
  }, [src]);
  return (
    <span className="broker-config-logo">
      {url ? <img src={url} alt="Partner logo" /> : <span className="broker-config-logo__monogram">{fallback}</span>}
    </span>
  );
};

const BrokerSettings = () => {
  const profile = getBrokerProfile();
  const fileInputRef = useRef(null);
  const [broker, setBroker] = useState(null);
  const [canManageTeam, setCanManageTeam] = useState(Boolean(profile?.canManageTeam));
  const [loading, setLoading] = useState(true);
  const [logoBusy, setLogoBusy] = useState(false);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const latestProfile = getBrokerProfile() || {};
      const response = latestProfile.brokerId
        ? await BrokerService.brokerDetail(latestProfile.brokerId)
        : null;
      const nextBroker = response?.data || {};
      setBroker(nextBroker);
      setCanManageTeam(Boolean(latestProfile.isPrimaryAdmin || latestProfile.canManageTeam));
      // Reflect the current logo in the sidebar/chrome without a re-login.
      syncBrokerProfileLogo(brokerLogoSrc(nextBroker));
    } catch (error) {
      toastr.error(error?.message || "Unable to load configuration.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Configuration is primary-admin only; skip the (primary-only) broker detail
  // call for everyone else so the no-access state renders without a 401 logout.
  useEffect(() => {
    if (!getBrokerProfile()?.isPrimaryAdmin) { setLoading(false); return; }
    load();
  }, [load]);

  const uploadLogo = async (file) => {
    if (!file || !broker?._id) return;
    if (!file.type.startsWith("image/")) {
      toastr.error("Please choose a PNG or JPG image.");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toastr.error("Logo must be smaller than 2 MB.");
      return;
    }
    setLogoBusy(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("data", JSON.stringify({ _id: broker._id, field: BROKER_LOGO_FIELD, module: "broker" }));
      await doPOST("/api/file/single/upload", formData);
      toastr.success("Logo updated.");
      await load({ silent: true });
    } catch (error) {
      toastr.error(error?.message || "Unable to upload logo.");
    } finally {
      setLogoBusy(false);
    }
  };

  const removeLogo = async () => {
    if (!broker?._id) return;
    setLogoBusy(true);
    try {
      await doDELETE("/api/file/delete/single", { _id: broker._id, field: BROKER_LOGO_FIELD, module: "broker" });
      toastr.success("Logo removed.");
      await load({ silent: true });
    } catch (error) {
      toastr.error(error?.message || "Unable to remove logo.");
    } finally {
      setLogoBusy(false);
    }
  };

  const brokerName = broker?.displayName || broker?.legalName || profile?.brokerName || "Partner";
  const emailDomain = normalizeDomain(broker?.emailDomain);

  const orgRows = [
    { label: "Organization name", value: broker?.displayName || brokerName },
    { label: "Legal name", value: broker?.legalName || "Not provided" },
    { label: "Allowed email domain", value: emailDomain ? `@${emailDomain}` : "Not configured" },
    { label: "Status", value: titleCase(broker?.status || "active") },
    { label: "Created", value: formatDate(broker?.createdAt) },
  ];

  if (!loading && !canManageTeam) {
    return (
      <div className="broker-page broker-config-page">
        <header className="broker-page-header">
          <p>Workspace</p>
          <h1>Configuration</h1>
          <span className="broker-page-header__sub">
            Configuration is managed by your primary partner contact.
          </span>
        </header>
        <BrokerEmptyState
          icon={FaLock}
          title="Only your primary partner contact can manage configuration"
          description="You have full access to the dashboard and applications. Ask your primary contact to update branding or organization settings."
        />
      </div>
    );
  }

  return (
    <div className="broker-page broker-config-page">

      {loading ? (
        <div className="broker-panel"><BrokerSkeleton rows={6} variant="detail" /></div>
      ) : (
        <div className="broker-dashboard-columns">
          <section className="broker-panel broker-rise" style={{ animationDelay: "60ms" }}>
            <div className="broker-panel__header">
              <div>
                <h2>Branding</h2>
                <p className="broker-panel__subtitle">Shown across your partner portal and individual verification pages.</p>
              </div>
            </div>
            <div className="broker-config-branding">
              <LogoPreview src={brokerLogoSrc(broker)} fallback={initials(brokerName)} />
              <div className="broker-config-branding__body">
                <strong>{brokerName}</strong>
                {canManageTeam && broker?._id ? (
                  <>
                    <div className="broker-config-logo-actions">
                      <button
                        type="button"
                        className="btn btn-light"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={logoBusy}
                      >
                        <FaUpload aria-hidden="true" /> {brokerLogoSrc(broker) ? "Replace logo" : "Upload logo"}
                      </button>
                      {brokerLogoSrc(broker) ? (
                        <button type="button" className="broker-link-button broker-link-button--danger" onClick={removeLogo} disabled={logoBusy}>
                          <FaTrashAlt aria-hidden="true" /> Remove
                        </button>
                      ) : null}
                    </div>
                    <span className="broker-config-branding__hint">PNG or JPG, up to 2&nbsp;MB. Square logos look best.</span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg"
                      hidden
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = "";
                        uploadLogo(file);
                      }}
                    />
                  </>
                ) : (
                  <span className="broker-config-branding__hint">
                    {canManageTeam ? "Save the partner before uploading a logo." : "Logo changes are managed by your primary admin."}
                  </span>
                )}
              </div>
            </div>
          </section>

          <section className="broker-panel broker-rise" style={{ animationDelay: "120ms" }}>
            <div className="broker-panel__header">
              <div>
                <h2>Organization</h2>
                <p className="broker-panel__subtitle">Managed by FV Bank. Contact your FV Bank representative to change these.</p>
              </div>
            </div>
            {orgRows.map((row) => (
              <div className="broker-detail-row" key={row.label}>
                <span className="broker-detail-row__label">{row.label}</span>
                <span className="broker-detail-row__value">{row.value}</span>
              </div>
            ))}
            <div className="broker-detail-row">
              <span className="broker-detail-row__label">Signed in as</span>
              <span className="broker-detail-row__value">{profile?.email || "Not available"}</span>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default BrokerSettings;
